import React, { useContext, useState, useEffect, useRef } from 'react';
import propTypes from 'prop-types';
import styled from 'styled-components';
import * as Sentry from '@sentry/browser';
import { FormattedMessage } from 'react-intl';
import { Button, ButtonLink } from '../../../components/buttons';
import { UserSettingsContext } from '../../../contexts/UserSettingsContext';
import { QueryStringContext } from '../../../contexts/QueryStringContext';
import GlobalTheme from '../../../styled/GlobalTheme';
import { colors } from '../../../styled/theme';
import AuthService from '../../../services/auth/AuthService';
import AndroidFBFlow from '../../../services/auth/AndroidFBFlow';
import { generateRandomString } from '../../../utils';
import { getElementDispatcher } from '../../../utils/AndroidEventDispacher';
import { QUERY_KEYS, QUERY_VALUES } from '../../../utils/constants/settings';

export const FacebookLinkButton = styled(ButtonLink)`
  && {
    color: ${({ disabled }) => (disabled ? colors.gray90 : '#415DA5')} !important;
    width: 100%;
    text-decoration: underline;
    font-size: 14px;
    ${GlobalTheme.breakpoints.down('xs')} {
      font-size: 14px;
    }
  }
`;

export const FacebookSmallLabel = styled.p`
  margin: 0;
  font-size: 14px;
  margin-top: -4px;
  ${GlobalTheme.breakpoints.down('sm')}{
    margin-top: 0px;
    line-height: normal;
  }
`;

const FacebookButtonStyled = styled(Button)`
&& {
  width: 100%;
  background: ${({ theme }) => (theme.colors.blue)};
  margin-top: 0;
  padding: 15px 10px;
  height: 28px;
  &:hover {
    border: 2px solid transparent;
    background: ${({ theme }) => (theme.colors.blue)};
  }

  ${GlobalTheme.breakpoints.down('lg')}{
    font-size: 12px;
    ${({ isApp }) => isApp && `
      font-size: 12px;
      padding: 10px;
      height: auto;
    `}
  }
  @media (max-width: 767px) {
    ${({ isApp }) => isApp && `
      padding: 8px;
      max-width: 170px;
      font-size: 10px;
    `}
  }
  @media (max-width: 1366px) and (orientation: landscape) {
    ${({ isApp }) => isApp && `
      font-size: 12px;
      padding: 10px;
      height: auto;
    `}
  }
}
`;

const FacebookButton = ({
  PromoCode,
  buttonType,
  wantsFree,
  disabled,
  isApp,
  isAndroid,
}) => {
  const buttonRef = useRef();
  const { subsCurrency, countryCode, locale } = useContext(UserSettingsContext);
  const [randomString, setRandomString] = useState('');
  const { getQueryStringVal } = useContext(QueryStringContext);

  useEffect(() => {
    const rStr = generateRandomString(16);
    setRandomString(rStr);
  }, []);

  useEffect(() => {
      const ele = getElementDispatcher();
      if(ele){
        ele.addEventListener('AndroidCancelLogin',() => {
          // you could add any custom event on this case
        });
      }
  }, []);

  const facebookFlowExecution = async () => {
    try {
      let promocode = '';
      
      if (PromoCode) {
        promocode = PromoCode.isValid ? PromoCode.code : '';
      }
      const typeOfTrial = getQueryStringVal(QUERY_KEYS.tt);
      const isOptIn = (typeOfTrial === QUERY_VALUES.tt);
      const lsPayload = {
        wantsEmails: true,
        wantsFree,
        language: locale,
        promocode,
        currencyCode: subsCurrency,
        countryCode,
        isOptIn,
      };
      AuthService.setUserData(lsPayload);
      if(isAndroid){
        AndroidFBFlow();
      }else{
        AuthService.initFacebook(randomString);
      }
      
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  return (
    <>
      {buttonType === 'signup' && (
        <FacebookButtonStyled
          ref={buttonRef}
          onClick={() => {
            facebookFlowExecution();
          }}
          disabled={disabled}
        >
          <>
            <FormattedMessage
              id="onboarding.creteAccount.signUpWithFB"
              description="Create account button Login with FB"
              defaultMessage="Sign Up with Facebook"
            />
          </>
        </FacebookButtonStyled>
      )}
      {buttonType === 'login' && (
        <FacebookButtonStyled
          ref={buttonRef}
          onClick={() => {
            facebookFlowExecution();
          }}
          disabled={disabled}
        >
          <FormattedMessage
            id="login.facebook.button"
            defaultMessage="Log in with Facebook"
          />

        </FacebookButtonStyled>
      )}
      { (buttonType === 'loginApp' || buttonType === 'signupapp') && (
        <FacebookButtonStyled
          ref={buttonRef}
          onClick={() => {
            facebookFlowExecution();
          }}
          disabled={disabled}
          isApp={isApp}
        >
          { (buttonType === 'loginApp') && (
            <FormattedMessage
              id="login.facebook.button"
              defaultMessage="Log in with Facebook"
            />
          )}

          { (buttonType === 'signupapp') && (
            <FormattedMessage
              id="onboarding.creteAccount.signUpWithFB"
              defaultMessage="Sign Up with Facebook"
            />
          )}

        </FacebookButtonStyled>
      )}
    </>
  );
};

FacebookButton.propTypes = {
  PromoCode: propTypes.instanceOf(Object),
  buttonType: propTypes.string,
  wantsFree: propTypes.bool,
  disabled: propTypes.bool,
  isApp: propTypes.bool,
};

FacebookButton.defaultProps = {
  PromoCode: {},
  buttonType: 'signup',
  wantsFree: false,
  disabled: false,
  isApp: false,
};

export default FacebookButton;
